/* eslint-disable max-len */
import { Col, Row, message } from 'antd';
import { Formik } from 'formik';
import { isEmpty } from 'lodash/fp';
import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import * as permissionKind from '../../../../../shared/permissions';
import {
    type AppointmentModuleEmailContentSpecsFragment,
    type AppointmentModuleInDealerSpecsFragment,
    type DealerTranslatedStringSettingDataFragment,
} from '../../../../api/fragments';
import { type DealerWithPermissionsFragmentFragment } from '../../../../api/fragments/DealerWithPermissionsFragment';
import { useUpdateAppointmentModuleEmailContentMutation } from '../../../../api/mutations/updateAppointmentModuleEmailContent';
import {
    type AppointmentModuleEmailContentCustomerInput,
    EmailContentUpdateType,
    type TranslatedStringInput,
} from '../../../../api/types';
import Form from '../../../../components/fields/Form';
import TranslatedTextAreaField from '../../../../components/fields/TranslatedTextAreaField';
import CollapsibleWrapper, { Panel } from '../../../../components/wrappers/CollapsibleWrapper';
import hasPermissions from '../../../../utilities/hasPermissions';
import useHandleError from '../../../../utilities/useHandleError';

export type DealerAssignmentAppointmentFormProps = {
    module: AppointmentModuleInDealerSpecsFragment;
    dealer: DealerWithPermissionsFragmentFragment;
};

type DealerAppointmentEmailInput = {
    subject?: TranslatedStringInput;
    introTitle?: TranslatedStringInput;
    contentText?: TranslatedStringInput;
};

type DealerAppointmentEmailFinderReservationInput = DealerAppointmentEmailInput & {
    listTestDriveTitle?: TranslatedStringInput;
    listTestDriveItem?: TranslatedStringInput;
};

type FormValues = {
    customer: {
        [key in keyof AppointmentModuleEmailContentCustomerInput]: DealerAppointmentEmailInput;
    };
    salesPerson: {
        submitConfirmation: DealerAppointmentEmailInput;
        finderReservation: DealerAppointmentEmailFinderReservationInput;
    };
};

const getEmailContentItem = (item: DealerTranslatedStringSettingDataFragment, dealerId: string) =>
    item.overrides.find(item => item.dealerId === dealerId)?.value ?? {
        defaultValue: '',
        overrides: [],
    };

const getEmailContent = (
    emailContent: Omit<AppointmentModuleEmailContentSpecsFragment, '__typename'>,
    dealerId: string
) => ({
    subject: getEmailContentItem(emailContent.subject, dealerId),
    introTitle: getEmailContentItem(emailContent.introTitle, dealerId),
    contentText: getEmailContentItem(emailContent.contentText, dealerId),
});

const addInputToOverride = (
    dealerId: string,
    originEmailContentItem: DealerTranslatedStringSettingDataFragment,
    translatedInput: TranslatedStringInput
) => ({
    ...originEmailContentItem,
    overrides: [
        ...originEmailContentItem.overrides.filter(item => item.dealerId !== dealerId),
        !isEmpty(translatedInput.defaultValue) && {
            dealerId,
            value: translatedInput,
        },
    ].filter(Boolean),
});

const setEmailItem = (
    dealerId: string,
    input: DealerAppointmentEmailInput,
    originEmailContent: Omit<AppointmentModuleEmailContentSpecsFragment, '__typename'>
) => ({
    subject: addInputToOverride(dealerId, originEmailContent.subject, input.subject),
    introTitle: addInputToOverride(dealerId, originEmailContent.introTitle, input.introTitle),
    contentText: addInputToOverride(dealerId, originEmailContent.contentText, input.contentText),
});

type DealerAppointmentEmailFieldProps = {
    path:
        | 'customer.submitConfirmation'
        | 'customer.endTestDrive'
        | 'customer.bookingAmendment'
        | 'customer.bookingConfirmation'
        | 'salesPerson.submitConfirmation'
        | 'salesPerson.finderReservation';
    children?: React.ReactNode;
    disabled?: boolean;
};

const DealerAppointmentEmailFields = ({ path, children, disabled }: DealerAppointmentEmailFieldProps) => {
    const { t } = useTranslation('appointmentModuleDetails');

    const getName = useCallback((name: string) => `${path}.${name}`, [path]);
    const getTranslationKey = useCallback((name: string) => `appointmentModuleDetails:fields.emails.${name}`, []);

    return (
        <Row gutter={10}>
            <Col lg={8} xs={24}>
                <TranslatedTextAreaField
                    {...t(getTranslationKey('subject'), { returnObjects: true })}
                    autoSize={{ minRows: 2, maxRows: 6 }}
                    disabled={disabled}
                    name={getName('subject')}
                />
            </Col>
            <Col lg={8} xs={24}>
                <TranslatedTextAreaField
                    {...t(getTranslationKey('introTitle'), { returnObjects: true })}
                    autoSize={{ minRows: 2, maxRows: 6 }}
                    disabled={disabled}
                    name={getName('introTitle')}
                />
            </Col>
            <Col lg={8} xs={24}>
                <TranslatedTextAreaField
                    {...t(getTranslationKey('contentText'), { returnObjects: true })}
                    autoSize={{ minRows: 2, maxRows: 6 }}
                    disabled={disabled}
                    name={getName('contentText')}
                    withContentRefinement
                />
            </Col>
            {children}
        </Row>
    );
};

const DealerAssignmentAppointmentForm = ({ module, dealer }: DealerAssignmentAppointmentFormProps) => {
    const { t } = useTranslation(['dealershipManagement', 'appointmentModuleDetails', 'moduleDetails']);

    const [updateAppointmentModuleEmailContent] = useUpdateAppointmentModuleEmailContentMutation();

    // initialize finance products and dealers for the dealer
    const initialValues: FormValues = useMemo(
        () => ({
            customer: {
                submitConfirmation: getEmailContent(module.emailContents.customer.submitConfirmation, dealer.id),
                endTestDrive: getEmailContent(module.emailContents.customer.endTestDrive, dealer.id),
                bookingAmendment: getEmailContent(module.emailContents.customer.bookingAmendment, dealer.id),
                bookingConfirmation: getEmailContent(module.emailContents.customer.bookingConfirmation, dealer.id),
            },
            salesPerson: {
                submitConfirmation: getEmailContent(module.emailContents.salesPerson.submitConfirmation, dealer.id),
                finderReservation: {
                    ...getEmailContent(module.emailContents.salesPerson.finderReservation, dealer.id),
                    listTestDriveTitle: getEmailContentItem(
                        module.emailContents.salesPerson.finderReservation.listTestDriveTitle,
                        dealer.id
                    ),
                    listTestDriveItem: getEmailContentItem(
                        module.emailContents.salesPerson.finderReservation.listTestDriveItem,
                        dealer.id
                    ),
                },
            },
        }),
        [dealer.id, module.emailContents]
    );

    const onSubmit = useHandleError<FormValues>(
        async values => {
            const { customer, salesPerson } = values;

            // submitting message
            message.loading({
                content: t('dealershipManagement:messages.updateSubmitting'),
                key: 'primary',
                duration: 0,
            });

            await updateAppointmentModuleEmailContent({
                variables: {
                    settings: {
                        emailContentUpdateType: EmailContentUpdateType.Dealer,
                        dealerId: dealer.id,
                        customer: {
                            submitConfirmation: setEmailItem(
                                dealer.id,
                                customer.submitConfirmation,
                                module.emailContents.customer.submitConfirmation
                            ),
                            endTestDrive: setEmailItem(
                                dealer.id,
                                customer.endTestDrive,
                                module.emailContents.customer.endTestDrive
                            ),
                            bookingAmendment: setEmailItem(
                                dealer.id,
                                customer.bookingAmendment,
                                module.emailContents.customer.bookingAmendment
                            ),
                            bookingConfirmation: setEmailItem(
                                dealer.id,
                                customer.bookingConfirmation,
                                module.emailContents.customer.bookingConfirmation
                            ),
                        },
                        salesPerson: {
                            submitConfirmation: setEmailItem(
                                dealer.id,
                                salesPerson.submitConfirmation,
                                module.emailContents.salesPerson.submitConfirmation
                            ),
                            finderReservation: {
                                ...setEmailItem(
                                    dealer.id,
                                    salesPerson.finderReservation,
                                    module.emailContents.salesPerson.finderReservation
                                ),
                                listTestDriveTitle: addInputToOverride(
                                    dealer.id,
                                    module.emailContents.salesPerson.finderReservation.listTestDriveTitle,
                                    salesPerson.finderReservation.listTestDriveTitle
                                ),
                                listTestDriveItem: addInputToOverride(
                                    dealer.id,
                                    module.emailContents.salesPerson.finderReservation.listTestDriveItem,
                                    salesPerson.finderReservation.listTestDriveItem
                                ),
                            },
                        },
                    },
                    moduleId: module.id,
                },
            });

            // show successful message
            message.success({
                content: t('dealershipManagement:messages.updateSuccessful'),
                key: 'primary',
            });
        },
        [t, module.emailContents, module.id, updateAppointmentModuleEmailContent, dealer.id]
    );

    const formId = `dealerForm-${module.id}`;
    const hasUpdatePermission = hasPermissions(dealer.permissions, [permissionKind.updateDealer]);

    return (
        <Formik<FormValues> initialValues={initialValues} onSubmit={onSubmit} enableReinitialize>
            {({ handleSubmit }) => (
                <Form id={formId} name={formId} onSubmitCapture={handleSubmit}>
                    <CollapsibleWrapper defaultActiveKey={['emailContent']}>
                        <Panel key="emailContent" header={t('appointmentModuleDetails:tabs.email.title')}>
                            <CollapsibleWrapper
                                defaultActiveKey={[
                                    'emailContentsCustomerSubmitConfirmation',
                                    'emailContentsCustomerEndTestDrive',
                                    'emailContentsCustomerBookingConfirmation',
                                    'emailContentsCustomerBookingAmendment',
                                    'emailContentsSalesPersonSubmitConfirmation',
                                    'emailContentsSalesPersonFinderReservation',
                                ]}
                            >
                                <Panel
                                    key="emailContentsCustomerSubmitConfirmation"
                                    header={t('appointmentModuleDetails:sections.emails.customer.submitConfirmation')}
                                >
                                    <DealerAppointmentEmailFields
                                        disabled={!hasUpdatePermission}
                                        path="customer.submitConfirmation"
                                    />
                                </Panel>
                                <Panel
                                    key="emailContentsCustomerEndTestDrive"
                                    header={t('appointmentModuleDetails:sections.emails.customer.endTestDrive')}
                                >
                                    <DealerAppointmentEmailFields
                                        disabled={!hasUpdatePermission}
                                        path="customer.endTestDrive"
                                    />
                                </Panel>
                                <Panel
                                    key="emailContentsCustomerBookingConfirmation"
                                    header={t('appointmentModuleDetails:sections.emails.customer.bookingConfirmation')}
                                >
                                    <DealerAppointmentEmailFields
                                        disabled={!hasUpdatePermission}
                                        path="customer.bookingConfirmation"
                                    />
                                </Panel>
                                <Panel
                                    key="emailContentsCustomerBookingAmendment"
                                    header={t('appointmentModuleDetails:sections.emails.customer.bookingAmendment')}
                                >
                                    <DealerAppointmentEmailFields
                                        disabled={!hasUpdatePermission}
                                        path="customer.bookingAmendment"
                                    />
                                </Panel>
                                <Panel
                                    key="emailContentsSalesPersonSubmitConfirmation"
                                    header={t(
                                        'appointmentModuleDetails:sections.emails.salesPerson.submitConfirmation'
                                    )}
                                >
                                    <DealerAppointmentEmailFields
                                        disabled={!hasUpdatePermission}
                                        path="salesPerson.submitConfirmation"
                                    />
                                </Panel>
                                <Panel
                                    key="emailContentsSalesPersonFinderReservation"
                                    header={t('appointmentModuleDetails:sections.emails.salesPerson.finderReservation')}
                                >
                                    <DealerAppointmentEmailFields
                                        disabled={!hasUpdatePermission}
                                        path="salesPerson.finderReservation"
                                    >
                                        <Col lg={8} xs={24}>
                                            <TranslatedTextAreaField
                                                {...t('appointmentModuleDetails:fields.emails.listTestDriveTitle', {
                                                    returnObjects: true,
                                                })}
                                                autoSize={{ minRows: 2, maxRows: 6 }}
                                                disabled={!hasUpdatePermission}
                                                name="salesPerson.finderReservation.listTestDriveTitle"
                                            />
                                        </Col>
                                        <Col lg={8} xs={24}>
                                            <TranslatedTextAreaField
                                                {...t('appointmentModuleDetails:fields.emails.listTestDriveItem', {
                                                    returnObjects: true,
                                                })}
                                                autoSize={{ minRows: 2, maxRows: 6 }}
                                                disabled={!hasUpdatePermission}
                                                name="salesPerson.finderReservation.listTestDriveItem"
                                            />
                                        </Col>
                                    </DealerAppointmentEmailFields>
                                </Panel>
                            </CollapsibleWrapper>
                        </Panel>
                    </CollapsibleWrapper>
                </Form>
            )}
        </Formik>
    );
};

export default DealerAssignmentAppointmentForm;
